import React from "react";
import { Skeleton, SkeletonText, Stack } from "@chakra-ui/react";
import ResponsiveFormStack from "../common/ResponsiveFormStack";
import { PreviewDropdownFormControl } from "../common/FormControlComponents";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/macro";
import { useParticipantFeesQuery } from "../../hooks/useParticipantFeesQuery";
import { PartecipantFeeRequest } from "../../types/fee.types";
import CurrencyContext from "../../context/CurrencyContext";
import { formatPrice } from "../../utils/price-formatter";
import {
  ModuleChangeStatus,
  ModuleSectionHeader,
} from "../common/ModuleToggleIndicator";

interface AttendanceFeesProps {
  congressId: number;
  handleChange: React.ChangeEventHandler<HTMLSelectElement | HTMLInputElement>;
  setFieldValue: Function;
  fee: PartecipantFeeRequest;
  isEditMode?: boolean;
  moduleChange?: ModuleChangeStatus;
}

const AttendanceFees: React.FC<AttendanceFeesProps> = ({
  congressId,
  setFieldValue,
  fee,
  isEditMode = false,
  moduleChange = "unchanged",
}) => {
  const { i18n } = useLingui();
  const { fees, areParticipantFeesLoading } = useParticipantFeesQuery(
    congressId,
    isEditMode,
  );

  const currency = React.useContext(CurrencyContext);
  const participantFees = React.useMemo(
    () =>
      fees.map((f) => ({
        id: f.id,
        leftText: f.name,
        rightText: `${formatPrice(f.totalPrice)} ${currency}`,
      })),
    [fees, currency],
  );

  const isSoftRequired = React.useMemo(
    () => isEditMode && moduleChange === "enabled",
    [isEditMode, moduleChange],
  );
  const hasAnyFieldFilled = !!(
    fee.participationFeeId != null && fee.participationFeeId !== 0
  );
  const isFeeSoftRequired = isSoftRequired && !hasAnyFieldFilled;
  const isFeeHardRequired = !isSoftRequired || hasAnyFieldFilled;
  const defaultPreviewText = React.useMemo(() => {
    if (isSoftRequired && !hasAnyFieldFilled) {
      return i18n._("Originally not selected");
    }
    return i18n._("Please select!");
  }, [isSoftRequired, hasAnyFieldFilled, i18n]);

  return (
    <Stack>
      <ModuleSectionHeader
        title={<Trans>Attendance Fee</Trans>}
        change={moduleChange}
      />
      <ResponsiveFormStack
        firstElement={
          <PreviewDropdownFormControl
            id={"attendanceFees"}
            label={i18n._("Attendance Fees")}
            values={participantFees}
            fieldName="participantFee.participationFeeId"
            setFieldValue={setFieldValue}
            isRequired={isFeeHardRequired}
            isSoftRequired={isFeeSoftRequired}
            selectedId={fee.participationFeeId}
            defaultPreviewText={defaultPreviewText}
            testId="attendance-fees-dropdown"
          />
        }
      />
      {areParticipantFeesLoading && (
        <>
          <Skeleton height="50px" />
          <SkeletonText mt="4" noOfLines={2} spacing="4" skeletonHeight="2" />
          <Skeleton height="50px" />
        </>
      )}
    </Stack>
  );
};

export default AttendanceFees;
